import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import QueryString from 'qs';
import { useHistory } from 'react-router';

import { definitions } from '@luxuryescapes/contract-svc-promo';

import { getPromoDisplayCheckResults } from '~/services/PromoService';

import useCurrentBrand from './useCurrentBrand';

export type PromoDisplayConfigResult = definitions['PromoDisplayCheckResult'];

export type PromoDisplayCheckResultsResponse = {
  result:
    | {
        promoDisplayCheckResults: Array<PromoDisplayConfigResult>;
        total: number;
      }
    | undefined;
  isLoading: boolean;
  error: string | null;
  onSubmit: (filter: GetPromoDisplayCheckResultsFilter) => void;
};

export type GetPromoDisplayCheckResultsFilter = {
  page: number;
  limit: number;
  promoDisplayConfigId?: string;
  region?: string;
  resultCode?: string;
  displayOptionKey?: string;
};

const clearEmptyFilterParams = (filter: GetPromoDisplayCheckResultsFilter): GetPromoDisplayCheckResultsFilter => {
  return Object.fromEntries(
    Object.entries(filter).filter(([_, value]) => value !== undefined && value !== ''),
  ) as GetPromoDisplayCheckResultsFilter;
};

export const usePromoDisplayCheckResults = (
  filter: GetPromoDisplayCheckResultsFilter,
): PromoDisplayCheckResultsResponse => {
  const history = useHistory();
  const brand = useCurrentBrand();
  const { enqueueSnackbar } = useSnackbar();
  const { data, isLoading, error } = useQuery({
    queryKey: ['promoDisplayCheckResults', filter],

    queryFn: () => getPromoDisplayCheckResults(clearEmptyFilterParams(filter)),
  });

  return {
    onSubmit: (newFilter: GetPromoDisplayCheckResultsFilter) => {
      const filters = QueryString.stringify(Object.assign(clearEmptyFilterParams(newFilter), { brand }));
      history.push(`/marketing/offer-metrics?tab=3&${filters}`);
      return getPromoDisplayCheckResults(clearEmptyFilterParams(newFilter)).catch((error) => {
        enqueueSnackbar(JSON.stringify(error), { variant: 'error' });
      });
    },
    result: data?.result,
    isLoading,
    error: error ? error.message : null,
  };
};

export default usePromoDisplayCheckResults;
